import React, { useMemo, useRef, useState } from 'react';

import { widgetComponents } from 'config/widgetConfigurations/widgetComponents';
import constants from 'constants/constants';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useCustomLatestCompareToVersions } from 'hooks/useCustomLatestCompareToVersions';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { useGetComponentKeysWithoutProjectAndContract } from 'hooks/useGetComponentKeysWithoutProjectAndContract';

import { useConfigurationStore } from 'store/configurationsStore';

import { UseMutationAddWidgetLayoutToReport } from 'api/mutations/addWidgetLayoutToReport.mutation';
import { useMutationSetComponent } from 'api/mutations/setComponent.mutation';

import { BottomHeaderSection } from 'components/common/PageContainer/BottomHeaderSection';
import { HeaderWrapper } from 'components/common/PageContainer/HeaderWrapper';
import { MainContent } from 'components/common/PageContainer/MainContent';
import PageContainer from 'components/common/PageContainer/PageContainer';
import { TreeCrumbs } from 'components/common/TreeCrumbs/TreeCrumbs';

import { IComponentKey, IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ConfirmWidgetContainer } from 'components/Dashboards/Reporting/confirmWidgetContainer/ConfirmWidgetContainer';
import { ReportWidgetRef, UpdateCreateWidget, Widget } from 'components/Dashboards/Reporting/reporting.types';
import { GlobalFiltersInReport } from 'components/pages/ConfirmWidgetPage/components/ReportGlobalFilters/GlobalFiltersInReport';
import { useWidgetGlobalFilters } from 'components/pages/ConfirmWidgetPage/components/ReportGlobalFilters/hooks/useWidgetGlobalFilters';
import { ConfirmWidgetHeader } from 'components/pages/ConfirmWidgetPage/components/confirmWidgetHeader/ConfirmWidgetHeader';
import { useProjectContractSelection } from 'components/pages/ConfirmWidgetPage/hooks/useProjectContractSelection';
import { ReportCardRoutingModal } from 'components/pages/ReportPage/ReportCardRoutingModal';
import { ScrollToBottomUrlQueryValues } from 'components/pages/ReportPage/hooks/useScrollToBottomOfReport';

export interface ConfirmWidgetPageUrlParams {
    componentKey: string;
    reportId: string;
}

interface Props {
    widget?: Widget;
}

export const ConfirmWidgetPage = ({ widget }: Props) => {
    const COMPONENT_KEYS_WITHOUT_PROJECT_AND_CONTRACT = useGetComponentKeysWithoutProjectAndContract();
    const reportWidgetRef = useRef<ReportWidgetRef | null>(null);
    const { componentKey: componentKeyUrlParam, reportId: reportIdUrlParam } = useParams<ConfirmWidgetPageUrlParams>();
    const isEditExistingWidget = widget && widget.id !== null;
    const resolvedComponentKey = isEditExistingWidget ? widget?.componentKey : componentKeyUrlParam;
    const [isActionButtonDisabled, setIsActionButtonDisabled] = useState(false);
    const isWidgetWithoutProjectAndContract = COMPONENT_KEYS_WITHOUT_PROJECT_AND_CONTRACT.includes(
        resolvedComponentKey as IComponentKey,
    );
    const configurations = useConfigurationStore();

    const history = useHistory();
    const queryClient = useQueryClient();
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    //queries
    const { mutateAsync: mutateSetWidgetInReport } = useMutationSetComponent();
    const { mutateAsync: addWidgetLayoutToReport } = UseMutationAddWidgetLayoutToReport();

    //version selection
    const { selectedProject, selectedContract, onSelectionFinish, projectHierarchyList } = useProjectContractSelection({
        projectId: widget?.projectId,
        contractId: widget?.contractId,
    });
    const shouldRenderProjectAndContractSelection =
        selectedContract && projectHierarchyList && !isWidgetWithoutProjectAndContract;

    // Widget versions, note:Widgets in report always reference the latest version and the comparison version
    const { latestVersionId, compareVersionId } = useCustomLatestCompareToVersions({
        projectId: selectedProject?.id,
        contractId: selectedContract?.id,
        componentKey: resolvedComponentKey as keyof IHashmap,
    });

    // get real component from component map via componentKey
    const componentObj = useMemo(() => {
        if (Boolean(resolvedComponentKey) && configurations[resolvedComponentKey]) {
            return configurations[resolvedComponentKey];
        }
        return null;
    }, []);
    if (!componentObj) return null;
    const { filterConfig: widgetGlobalFiltersConfig, title: defaultWidgetNameFromConfigFile } = componentObj;
    const { Component: Widget } = widgetComponents[resolvedComponentKey];

    //global filters state
    const { globalFilters, setGlobalFilters } = useWidgetGlobalFilters({
        widgetGlobalFiltersConfig,
        widget,
        defaultWidgetNameFromConfigFile,
    });

    // get widget state from ref connected to widget
    const getWidgetState = () => {
        return JSON.stringify(reportWidgetRef.current?.getWidgetState()) ?? '';
    };

    const getWidgetPayload = () => {
        return JSON.stringify(reportWidgetRef.current?.getWidgetPayload()) ?? '';
    };

    const onNavigateBackToReport = (widgetId?: number, componentKey?: string): void => {
        const query =
            widgetId && componentKey
                ? {
                      [ScrollToBottomUrlQueryValues.scrollToWidget]: widgetId,
                      [ScrollToBottomUrlQueryValues.componentKey]: componentKey,
                  }
                : null;
        const url = AppRoutes.buildPathWithQuery([AppRoutes.DASHBOARD_REPORTING_PATH, reportIdUrlParam], query);
        history.push(url);
    };

    const updateReportLayout = async (reportId: number, widgetId: number) => {
        // When Editing existing widget there is no need to change report layout,
        // When creating a new widget, the component is added to the end of the layout and the report's layout needs to be updated
        // Add initial size( w,h value) for each new component based on the component key.
        const shouldInsertAtTop = resolvedComponentKey === configurations.reportSummary.componentKey;

        await addWidgetLayoutToReport(
            {
                reportId,
                widgetId: widgetId.toString(),
                insertAtTop: shouldInsertAtTop,
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries(['getReport', { id: reportId }]);
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    const runWidgetCallback = async (newWidgetId: number) => {
        //run onAddWidgetCallback to save widget columns when new widget created
        const onAddWidgetCallback =
            reportWidgetRef && reportWidgetRef.current && reportWidgetRef.current.onAddWidgetCallback;
        if (onAddWidgetCallback) {
            onAddWidgetCallback(newWidgetId.toString());
        }
    };

    const runWidgetCallbackAfterAddOrEdit = async (newWidgetId: number) => {
        //run onAddWidgetCallback to save widget columns when new widget created or after edit
        const onAddOrEditWidgetCallback =
            reportWidgetRef && reportWidgetRef.current && reportWidgetRef.current.onAddOrEditWidgetCallback;
        if (onAddOrEditWidgetCallback) {
            onAddOrEditWidgetCallback(newWidgetId.toString());
        }
    };

    const invalidateDependentQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['getReport', { id: widget?.reportId }] }),
            queryClient.invalidateQueries({ queryKey: ['getReportList'] }),
            queryClient.invalidateQueries({ queryKey: ['getReportAccessStatus'] }),
        ]);
    };

    const onSave = async () => {
        const widgetToBeSaved: UpdateCreateWidget = {
            id: widget?.id ?? null,
            componentKey: resolvedComponentKey,
            contractId: isWidgetWithoutProjectAndContract
                ? constants.NO_PROJECT_CONTRACT_ID
                : (selectedContract?.id as number),
            projectId: isWidgetWithoutProjectAndContract
                ? constants.NO_PROJECT_CONTRACT_ID
                : (selectedProject?.id as number),
            reportId: Number(reportIdUrlParam),
            params: getWidgetState(),
            description: widget?.description ?? '',
            showNarrative: widget?.showNarrative ?? false,
            name: reportWidgetRef.current?.getWidgetTitle?.() ?? '',
            payload: getWidgetPayload(),
            endpoint: reportWidgetRef.current?.getWidgetEndpoint?.(),
        };
        setIsActionButtonDisabled(true);
        await mutateSetWidgetInReport(widgetToBeSaved, {
            onSuccess: async (response) => {
                const isNewWidget = !isEditExistingWidget;

                // Case: new widget added
                if (isNewWidget) {
                    const componentName = componentObj?.title || defaultWidgetNameFromConfigFile || 'Unknown';
                    sendEventWithDimensions({
                        category: 'Reporting',
                        action: 'Component Confirmed',
                        label: componentName,
                    });

                    const createdWidgetId = response.id;
                    const reportId = response?.reportId;

                    await runWidgetCallbackAfterAddOrEdit(createdWidgetId);
                    await runWidgetCallback(createdWidgetId);
                    await updateReportLayout(reportId, createdWidgetId);

                    await invalidateDependentQueries();
                    onNavigateBackToReport(createdWidgetId, resolvedComponentKey);
                } else {
                    await runWidgetCallbackAfterAddOrEdit(widgetToBeSaved.id);
                    // Case: edit existing widget
                    handleSnackBar(
                        `Your edit of component '${widgetToBeSaved.name}' was successfully saved.`,
                        'success',
                    );
                    await invalidateDependentQueries();
                    onNavigateBackToReport();
                }
                setIsActionButtonDisabled(false);
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
                setIsActionButtonDisabled(false);
            },
        });
    };

    return (
        <PageContainer>
            <HeaderWrapper>
                <BottomHeaderSection>
                    <ConfirmWidgetHeader
                        isActionButtonDisabled={isActionButtonDisabled}
                        isEditExistingWidget={isEditExistingWidget}
                        onSave={onSave}
                    />
                </BottomHeaderSection>
            </HeaderWrapper>
            <MainContent>
                <ConfirmWidgetContainer>
                    {shouldRenderProjectAndContractSelection && (
                        <TreeCrumbs
                            selectedNode={selectedContract.uuid}
                            data={projectHierarchyList}
                            onSelectionFinish={onSelectionFinish}
                        />
                    )}
                    {widgetGlobalFiltersConfig && (
                        <GlobalFiltersInReport
                            globalFilters={globalFilters}
                            setGlobalFilters={setGlobalFilters}
                            widgetGlobalFiltersConfig={widgetGlobalFiltersConfig}
                            latestVersionId={latestVersionId}
                            compareVersionId={compareVersionId}
                            selectedProject={selectedProject}
                            selectedContract={selectedContract}
                            widget={widget}
                            componentKey={resolvedComponentKey}
                        />
                    )}
                    <Widget
                        widgetViewMode={isEditExistingWidget ? WidgetView.reportEditWidget : WidgetView.reportAddWidget}
                        projectId={selectedProject?.id}
                        contractId={selectedContract?.id}
                        latestVersionId={latestVersionId}
                        compareVersionId={compareVersionId}
                        reportWidgetRef={reportWidgetRef}
                        localFilters={isEditExistingWidget ? widget?.params : null}
                        globalFilterData={globalFilters}
                        title={widget?.name}
                    />
                </ConfirmWidgetContainer>
            </MainContent>
            <ReportCardRoutingModal />
        </PageContainer>
    );
};
